import React from 'react';
import cx from 'classnames';

import Icon, { IconProps } from '../Icon';
import BaseButton, { BaseButtonProps } from '../BaseButton';
import Box from '../../Box';
import LoadingSpinnerNoBits from '../../LoadingSpinnerNoBits';
import Tooltip, { Props as TooltipProps } from '../../Tooltip';

import styles from './IconButton.module.scss';

function outputIconSize(btnSize: number) {
  let iconSize;
  switch (btnSize) {
    case 56:
      iconSize = 20;
      break;
    case 48:
      iconSize = 18;
      break;
    case 40:
      iconSize = 16;
      break;
    case 32:
      iconSize = 16;
      break;
    default:
      iconSize = 14;
  }
  return iconSize;
}
export interface IconButtonProps
  extends Omit<BaseButtonProps, 'children' | 'intent'> {
  /**
   * AccessibilityLabel should provide meaningful context about button to those who rely on a screen reader to interact with the UI
   * */
  accessibilityLabel: string;
  /**
   * Escape hatch to give custom styles to specific elements.
   */
  classes?: {
    iconClassName?: string;
    textClassName?: string;
  };
  /**
   * Escape hatch to add props to internal elements.
   */
  components?: {
    tooltip: {
      text?: string;
      placement: TooltipProps['placement'];
    };
  };
  /**
   * If false, tooltip will not be visible.
   *
   * @default true
   */
  hasTooltip?: boolean;
  /**
   * Specify the icon that will appear.
   * @default chevron-right
   */
  icon: IconProps['appearance'] | React.ReactElement;
}

export const IconButton: React.FC<IconButtonProps> = ({
  accessibilityLabel,
  appearance = 'solid',
  className,
  classes,
  components = { tooltip: { placement: 'top' } },
  disabled,
  hasTooltip = true,
  icon,
  isLoading,
  size = 48,
  ...restProps
}) => {
  const iconSize = outputIconSize(size);

  const isTouchDevice: boolean =
    typeof matchMedia === 'function'
      ? matchMedia('(hover: none)').matches
      : false;

  const { tooltip } = components;

  return (
    <Tooltip
      dark
      placement={tooltip.placement}
      showOn={
        isTouchDevice || !hasTooltip || disabled || isLoading
          ? 'disabled'
          : ['hover', 'focus']
      }
      small
      target={
        <BaseButton
          className={cx(
            styles.iconButton,
            appearance && styles[appearance],
            isLoading && styles.isLoading,
            className,
          )}
          appearance={appearance}
          disabled={disabled}
          isLoading={isLoading}
          ariaLabel={accessibilityLabel}
          {...restProps}
        >
          <Box display='flex' className={styles[`size${size}`]}>
            {isLoading && (
              <LoadingSpinnerNoBits
                variant='single-color'
                className={styles.spinnerContainer}
                spinnerClassName={styles.spinner}
              />
            )}
            {typeof icon === 'string' ? (
              <Icon
                appearance={icon as IconProps['appearance']}
                className={cx(styles.icon, classes?.iconClassName)}
                size={iconSize as IconProps['size']}
              />
            ) : (
              React.cloneElement(icon, {
                className: cx(styles.icon, classes?.iconClassName),
              })
            )}
          </Box>
        </BaseButton>
      }
    >
      {tooltip.text || accessibilityLabel}
    </Tooltip>
  );
};

IconButton.displayName = 'IconButton';
export default IconButton;
export { styles };
